// external imports
import {decode as decodeHtmlEntities} from 'html-entities';
import moment from 'moment-timezone';
import {enumerate} from 'ts-transformer-enumerate';

// shared imports
import metaModel from '~/shared/src/utilities/metaModel';
import messageUtils from '~/shared/src/utilities/messageUtils';

// frontend imports
///////////////////////////
import {
    locale,
    addLocale
} from 'primereact/api';
import 'moment/locale/de';
import 'moment/locale/fr';
import 'moment/locale/it';
import 'moment/locale/es';
//////////

// backend imports
///////////////////////////
////////
////////////
//////////
/////////////////////////
////////////////////////////////////////
////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////
//////////

type LocalizedString = { [ language : string ] : string };
type Message = {
    Content : LocalizedString,
    Subject : LocalizedString,
    Title : LocalizedString
};
type Messages = { [ messageKey : string ] : Message };

type MessageContentType = 'Content' | 'Subject' | 'Title';

interface MessageCatalogContent {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    message_catalog : Messages,
    metamodel : Messages,
    format : Messages,
    other : Messages
}

type MessageType = keyof MessageCatalogContent;
const messageTypes = enumerate<MessageType>() as MessageType[];

export type Placeholders = { [ placeholder : string ] : string };

export type MessageCatalogMockupData = {
    [messageType in MessageType]? : Array<{ key : string; } & { [MessageSubType in keyof Message]? : Message[MessageSubType] }>;
};

let error : Function;

///////////////////////////
/////////////////////
/////////
error = console.error.bind( console );

//////////

class MessageCatalog {

    /**
     * The default language to use for translation methods
     */
    private _defaultLanguage : string = 'EN';

    /**
     * The language to use for translation methods
     */
    private _language : string = 'EN';

    /**
     * Getter for language
     */
    get language() : string {
        return this._language;
    }

    /**
     * Setter for language
     */
    set language( language : string ) {
        this._language = language;

///////////////////////////////////
        // set locale to PrimeReact
        locale( language );
        // set locale to moment
        moment.locale( language );
//////////////////
    }

    get hasData() : boolean {
        let retVal : boolean = false;

        for ( const messageType of messageTypes ) {
            if ( this._messages[ messageType ] && Object.keys( this._messages[ messageType ] ).length ) {
                retVal = true;
                break;
            }
        }

        return retVal;
    }

    /**
     * The supported languages overall
     */
    private _supportedLanguages : Array<string> = [];

    /**
     * The timestamp where the data was loaded from database
     */
    private _timestamp : number = 0;

    /**
     * The message catalog content
     */
    private _messages : MessageCatalogContent = {
        message_catalog: {},
        metamodel      : {},
        format         : {},
        other          : {}
    };

    private _messageCatalogKeys : { [messageType in MessageType]? : string[] } = {};

///////////////////////////////
////////////////////////
/////////////
//////////////////////////////////////

//////////////////////////////////////
/////////
////////////////////////////
//////////////////////////////
/////////
/////////////////
/////////////////////////////////////////
/////////
/////

///////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////
//////////////////////////////////////
//////////////////////////////////////
/////////////////////////////////////
////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////

///////////////////////////////////////////////

////////////////////////
////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////
////////////////////////////////////////////
/////////

/////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////
///////////////////////////////////////////////////////////////////////////////////
/////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////
///////////////////////////////////////////////////////////////////////////////////
/////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////
/////////////////////////////////////////////////////////////////////////////////
/////////

///////////////////////////////////
/////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////

//////////////////////////////////////////////////

/////////////////////////////////////////////////////////////////////////////////////////////////////

///////////////////////////////////////////
/////////////////////////////////////
////////////////////////////////////////////////////////////
///////////////////////////////////////////
//////////////////////////////////
//////////////////////////////////
/////////////////////////////////
////////////////////

///////////////////////////////////////

////////////////////////////////////
////////////////////////////////////
///////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////
//////////////////////////////
/////////////////////
/////////////////
/////////////

////////////////////////////////////
/////////////////////////////////////////////////////
////////////////////////////////////////
///////////////////////////////////////////////////////////////////

/////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

///////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////
/////////////////////////
/////////////////////
/////////////////
/////////////

//////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////

////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////
////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////
/////////////
/////////

//////////////////////////////////////////////////////////////////
/////////////////////////////////////////
/////////
/////////////////////////////////////
//////////////////////////////////////////////////////
/////////
//////////////
///////////////////////////////////////////////////////////////
/////////

///////////////////////////////////
///////////////////////////////////
/////////////////////////////////////////////////////////////////////
/////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////
/////

//////////////

    loadMockupData( mockupData : MessageCatalogMockupData ) {
        try {
            this.doLoadMockupData( mockupData );
        }
        catch ( errorMsg ) {
            error( errorMsg );
        }
        finally {
            this._timestamp = Date.now();
        }
    }

    private isMessageType( type : string ) : type is MessageType {
        return type in this._messages;
    }

    private doLoadMockupData( mockupData : MessageCatalogMockupData ) {
        // init supported languages
        if ( !this._supportedLanguages.length ) {
            this.setSupportedLanguages( Object.keys( global.config.common.supportedLanguages ) as string[] );
        }

///////////////////////////////////
        // add locale to PrimeReact
        this.setPrimeReactLocale();
//////////////////

        // extract data and add to messages
        const mockMessageTypes = Object.keys( mockupData ) as MessageType[];
        for ( const messageType of mockMessageTypes ) {
            for ( const message of mockupData[ messageType ]! ) {
                const messageId = message.key;

                this._messages[ messageType ][ messageId ] = Object.assign(
                    {
                        Content: {},
                        Subject: {},
                        Title  : {}
                    },
                    message
                );
            }
        }
    }

    /**
     * Returns a JSON string with the content of this.
     */
    toJsonString() : string {
        return JSON.stringify( this._messages );
    }

    /**
     * Loads the content for this given by JSON string.
     *
     * @param {string} data
     * @param {number} [timestamp=Date.now()]    The creation time of the data
     */
    fromJsonString( data : string, timestamp : number = Date.now() ) {
        let retVal : boolean = false;

        if ( !this._supportedLanguages.length ) {
            this.setSupportedLanguages( Object.keys( global.config.common.supportedLanguages ) as string[] );
        }

///////////////////////////////////
////////////////////////////////////////////////////
//////////////////

        try {
            const content = <MessageCatalogContent> JSON.parse( data );

            if ( !Object.keys( content.message_catalog ).length ) {
                throw new Error( 'MessageCatalog is empty' );
            }

            this._messages = content;
            retVal = true;

            //this.printMessageCount();
        }
        catch ( errorMsg ) {
            error( errorMsg );
        }
        finally {
            this._timestamp = timestamp;

///////////////////////////////////////
            // add locale to PrimeReact
            this.setPrimeReactLocale();
//////////////////////
        }

///////////////////////////////////
///////////////////////////////////
/////////////////////////////////////////////////////////////////////
/////////////////////
////////////////////////////////////////////////////////
//////////////////

        return retVal;
    }

    printMessageCount() {
        let count = 0;

        for ( const messageType of Object.keys( this._messages ) ) {
            for ( const messageId of Object.keys( this._messages[ messageType ] ) ) {
                for ( const messageContentType of Object.keys( this._messages[ messageType ][ messageId ] ) ) {
                    count += Object.keys( this._messages[ messageType ][ messageId ][ messageContentType ] ).length;
                }
            }
        }

///////////////////////////////////
////////////////////////////////////////////////////////////////
//////////////////

///////////////////////////////////
        console.info( `MessageCatalog contains ${count} entries` );
//////////////////
    }

    /**
     * Sets the messages for PrimeReact locale API
     */
    setPrimeReactLocale() {
        const language = this._language;

        for ( const languageId of this._supportedLanguages ) {
            this._language = languageId;

            addLocale( languageId, {
                accept            : __( 'YN.base.common.yes' ),
                reject            : __( 'YN.base.common.no' ),
                choose            : __( 'YN.base.common.choose' ),
                upload            : __( 'YN.base.common.upload' ),
                cancel            : __( 'YN.base.common.cancel' ),
                apply             : __( 'YN.base.common.apply' ),
                clear             : __( 'YN.base.common.clear' ),
                dayNames          : [
                    __( 'YN.base.common.DayNameMap.7' ),
                    __( 'YN.base.common.DayNameMap.1' ),
                    __( 'YN.base.common.DayNameMap.2' ),
                    __( 'YN.base.common.DayNameMap.3' ),
                    __( 'YN.base.common.DayNameMap.4' ),
                    __( 'YN.base.common.DayNameMap.5' ),
                    __( 'YN.base.common.DayNameMap.6' )
                ],
                dayNamesShort     : [
                    __( 'YN.base.common.ShortDayNameMap.7' ),
                    __( 'YN.base.common.ShortDayNameMap.1' ),
                    __( 'YN.base.common.ShortDayNameMap.2' ),
                    __( 'YN.base.common.ShortDayNameMap.3' ),
                    __( 'YN.base.common.ShortDayNameMap.4' ),
                    __( 'YN.base.common.ShortDayNameMap.5' ),
                    __( 'YN.base.common.ShortDayNameMap.6' )
                ],
                dayNamesMin       : [
                    __( 'YN.base.common.MinDayNameMap.7' ),
                    __( 'YN.base.common.MinDayNameMap.1' ),
                    __( 'YN.base.common.MinDayNameMap.2' ),
                    __( 'YN.base.common.MinDayNameMap.3' ),
                    __( 'YN.base.common.MinDayNameMap.4' ),
                    __( 'YN.base.common.MinDayNameMap.5' ),
                    __( 'YN.base.common.MinDayNameMap.6' )
                ],
                monthNames        : [
                    __( 'YN.base.common.MonthNameMap.1' ),
                    __( 'YN.base.common.MonthNameMap.2' ),
                    __( 'YN.base.common.MonthNameMap.3' ),
                    __( 'YN.base.common.MonthNameMap.4' ),
                    __( 'YN.base.common.MonthNameMap.5' ),
                    __( 'YN.base.common.MonthNameMap.6' ),
                    __( 'YN.base.common.MonthNameMap.7' ),
                    __( 'YN.base.common.MonthNameMap.8' ),
                    __( 'YN.base.common.MonthNameMap.9' ),
                    __( 'YN.base.common.MonthNameMap.10' ),
                    __( 'YN.base.common.MonthNameMap.11' ),
                    __( 'YN.base.common.MonthNameMap.12' )
                ],
                monthNamesShort   : [
                    __( 'YN.base.common.ShortMonthNameMap.1' ),
                    __( 'YN.base.common.ShortMonthNameMap.2' ),
                    __( 'YN.base.common.ShortMonthNameMap.3' ),
                    __( 'YN.base.common.ShortMonthNameMap.4' ),
                    __( 'YN.base.common.ShortMonthNameMap.5' ),
                    __( 'YN.base.common.ShortMonthNameMap.6' ),
                    __( 'YN.base.common.ShortMonthNameMap.7' ),
                    __( 'YN.base.common.ShortMonthNameMap.8' ),
                    __( 'YN.base.common.ShortMonthNameMap.9' ),
                    __( 'YN.base.common.ShortMonthNameMap.10' ),
                    __( 'YN.base.common.ShortMonthNameMap.11' ),
                    __( 'YN.base.common.ShortMonthNameMap.12' )
                ],
                now               : __( 'YN.base.common.now' ),
                today             : __( 'YN.base.common.today' ),
                weekHeader        : __( 'YN.base.common.PrimeReact.WeekHeader' ),
                firstDayOfWeek    : parseInt( __( 'YN.base.common.PrimeReact.FirstDayOfWeek' ) ),
                dateFormat        : __( 'YN.base.common.PrimeReact.DateFormat' ),
                weak              : __( 'YN.base.common.PrimeReact.weak' ),
                medium            : __( 'YN.base.common.PrimeReact.medium' ),
                strong            : __( 'YN.base.common.PrimeReact.strong' ),
                passwordPrompt    : __( 'YN.base.common.PrimeReact.passwordPrompt' ),
                emptyFilterMessage: __( 'YN.base.common.PrimeReact.emptyFilterMessage' ),
                emptyMessage      : __( 'YN.base.common.PrimeReact.emptyMessage' ),
                startsWith        : __( 'YN.base.operator.startsWith' ),
                contains          : __( 'YN.base.operator.contains' ),
                notContains       : __( 'YN.base.operator.notContains' ),
                endsWith          : __( 'YN.base.operator.endsWith' ),
                equals            : __( 'YN.base.operator.equals' ),
                notEquals         : __( 'YN.base.operator.notEquals' ),
                noFilter          : __( 'YN.base.operator.noFilter' ),
                lt                : __( 'YN.base.operator.lt' ),
                lte               : __( 'YN.base.operator.lte' ),
                gt                : __( 'YN.base.operator.gt' ),
                gte               : __( 'YN.base.operator.gte' ),
                dateIs            : __( 'YN.base.operator.dateIs' ),
                dateIsNot         : __( 'YN.base.operator.dateIsNot' ),
                dateBefore        : __( 'YN.base.operator.dateBefore' ),
                dateAfter         : __( 'YN.base.operator.dateAfter' ),
                custom            : __( 'YN.base.operator.custom' ),
                matchAll          : __( 'YN.base.operator.matchAll' ),
                matchAny          : __( 'YN.base.operator.matchAny' ),
                addRule           : __( 'YN.base.operator.addRule' ),
                removeRule        : __( 'YN.base.operator.removeRule' )
            } );
        }

        this._language = language;

///////////////////////////////////
        // set locale to PrimeReact
        locale( this._language );
//////////////////
    }

    /**
     * Setter for supportedLanguages
     */
    setSupportedLanguages( supportedLanguages? : Array<string> ) {
        supportedLanguages = supportedLanguages ?? [ 'DE', 'EN' ];

        this._supportedLanguages = !Array.isArray( supportedLanguages )
            ? Object.keys( supportedLanguages ) || []
            : ( [] as string[] ).concat( supportedLanguages );
    }

    /**
     * Getter for supportedLanguages
     */
    getSupportedLanguages() : Array<string> {
        return this._supportedLanguages;
    }

    /**
     * Getter for timestamp
     */
    getTimestamp() : number {
        return this._timestamp;
    }

    /**
     * Tries to get the localized string for given messageKey from message catalog otherwise
     * tries to extract local string.
     *
     * @param messageKey                    message identifier or localized string
     * @param [placeholders]                placeholders as key (without leading/trailing #) value pairs to be replaced in translated string
     * @param [dontDecodeHtmlEntities]      if flag is set, the message get's not html_entity_decoded
     * @param [prefix]                      prefix for messageKey
     * @param [language]                    language for translation
     *
     * @return                              localized text or the messageKey if not translatable
     */
    translate( messageKey : string, placeholders : Placeholders = {}, dontDecodeHtmlEntities : boolean = true, prefix : string = '' ) : string {
        let retVal;

        let msgId : string = '';

        messageKey = messageKey + '';

        this._messageCatalogKeys = this._messageCatalogKeys ?? Object.keys( this._messages[ 'message_catalog' ] ) as string[];

        if ( this._messages[ 'message_catalog' ] ) {
            const msgItem = this._messages[ 'message_catalog' ][ prefix + messageKey ];

            msgId = this.getEncodedMessageId( prefix + messageKey );

            // check messageKey for set language
            if ( msgItem?.Content?.[ this._language ] ) {
                retVal = msgItem.Content[ this._language ];
            }

            // check messageKey for default language
            else if ( msgItem?.Content?.[ this._defaultLanguage ] ) {
                retVal = msgItem.Content[ this._defaultLanguage ];
            }

            // check messageKey for first existing language entry
            else if ( Object.keys( msgItem?.Content ?? {} ).length ) {
                retVal = msgItem.Content[ Object.keys( msgItem?.Content )[ 0 ] ];
            }

            // check for {DE:xyz} notation
            else {
                retVal = this.extractLocalString( messageKey );
            }

            if ( !dontDecodeHtmlEntities ) {
                retVal = decodeHtmlEntities( retVal );
            }

            // replace placeholders if
            retVal = this.replacePlaceholders( retVal, this._language, placeholders );
        }
        else {
            msgId = this.getEncodedMessageId( messageKey );

            retVal = messageKey;
        }

        // try to translate ${__(...)} in retVal
        let match;
        while ( ( match = retVal.match( /(?:\$\{)?__\((?:\s(?:\'|")([^\)]*)(?:\'|")\s)\)(?:\})?/ ) ) ) {
            retVal = retVal.replace( match[ 0 ], __( match[ 1 ] ) );
        }

        // log message not translatable
        // TODO: this leads to not responding ide due to a lot of log pollution, throttle it
        /*if ( messageKey && messageKey === retVal && messageKey.indexOf( '.' ) !== -1 && ( /^(?!.*\.$)([a-zA-Z0-9_-]+(\.|$))+$/ ).test( messageKey ) ) {
///////////////////////////////////////
/////////////////////////////////////////////////////

///////////////////////////////////////////////////////////////////
//////////////////////////
                console.warn( 'MessageCatalog - not translatable: ' + messageKey );
///////////////////////////////////////////
/////////////
//////////////////////
        }*/

        if ( msgId ) {
            retVal += msgId;
        }

        return retVal;
    }

    /**
     * Wrapper method for getContent, which returns language specific day of week name.
     *
     * @param isoDayOfWeekNum       day of week (ISO) number
     * @param language              language identifier to get the day name for
     */
    getDayName( isoDayOfWeekNum : string, language : string = this._language ) : string {
        return this.getContent( `YN.base.common.DayNameMap.${isoDayOfWeekNum}`, language, {}, false, true );
    }

    /**
     * Wrapper method for getContent, which returns language specific month name.
     *
     * @param monthNum              month number
     * @param language              language identifier to get the month name for
     */
    getMonthName( monthNum : string, language : string = this._language ) : string {
        return this.getContent( `YN.base.common.MonthNameMap.${monthNum}`, language, {}, false, true );
    }

    /**
     * Wrapper method for getContent, which returns language specific salutation.
     *
     * @param salutation            salutation
     * @param language              language identifier to get the salutation for
     */
    getSalutation( salutation : string, language : string = this._language ) : string {
        return this.getContent( `YN.base.common.SalutationMap.${salutation}`, language, {}, false, true );
    }

    /**
     * Wrapper method for getContent, which returns MomentJS db timestamp format string.
     *
     * @param language              language identifier to get the message for
     */
    getDbTimestampFormatForMomentJS( language : string = this._language ) : string {
        return this.getContent( 'YN.base.common.MomentJS.DbTimestampFormat', language, {}, false, true, 'format' );
    }

    /**
     * Wrapper method for getContent, which returns MomentJS date format string.
     *
     * @param language              language identifier to get the message for
     */
    getDateFormatForMomentJS( language : string = this._language ) : string {
        return this.getContent( 'YN.base.common.MomentJS.DateFormat', language, {}, false, true, 'format' );
    }

    /**
     * Wrapper method for getContent, which returns MomentJS time format string.
     *
     * @param language              language identifier to get the message for
     */
    getTimeFormatForMomentJS( language : string = this._language ) : string {
        return this.getContent( 'YN.base.common.MomentJS.TimeFormat', language, {}, false, true, 'format' );
    }

    /**
     * Wrapper method for getMessage, which returns language specific Content value of specified Message item.
     *
     * @param messageKey            CI_KEY of Message item
     * @param language              language identifier to get the message for
     * @param placeholders          assoziative array with placeholders (key) and replacements (value)
     * @param multiLanguageFormat   true to return all set languages for the message
     *                              in special format: <english message (if)>[{<language short>:<message>},...]
     * @param raw                   true to return the message not processed (no placeholders will be replaced)
     * @param messageType           message type to get the message for
     * @param dontAppendEncodedMsgId
     */
    getContent( messageKey : string, language : string = this._language, placeholders : Placeholders = {}, multiLanguageFormat : boolean = false, raw : boolean = false, messageType : MessageType = 'message_catalog', dontAppendEncodedMsgId : boolean = true ) : string {
        return this.getMessage( messageKey, 'Content', language, placeholders, multiLanguageFormat, raw, messageType, dontAppendEncodedMsgId );
    }

    /**
     * Wrapper method for getMessage, which returns language specific Subject value of specified Message item.
     *
     * @param messageKey            CI_KEY of Message item
     * @param language              language identifier to get the message for
     * @param placeholders          assoziative array with placeholders (key) and replacements (value)
     * @param multiLanguageFormat   true to return all set languages for the message
     *                              in special format: <english message (if)>[{<language short>:<message>},...]
     * @param raw                   true to return the message not processed (no placeholders will be replaced)
     * @param messageType           message type to get the message for
     * @param dontAppendEncodedMsgId
     */
    getSubject( messageKey : string, language : string = this._language, placeholders : Placeholders = {}, multiLanguageFormat : boolean = false, raw : boolean = false, messageType : MessageType = 'message_catalog', dontAppendEncodedMsgId : boolean = true ) : string {
        return this.getMessage( messageKey, 'Subject', language, placeholders, multiLanguageFormat, raw, messageType, dontAppendEncodedMsgId );
    }

    /**
     * Wrapper method for getMessage, which returns language specific Title value of specified Message item.
     *
     * @param messageKey            CI_KEY of Message item
     * @param language              language identifier to get the message for
     * @param placeholders          assoziative array with placeholders (key) and replacements (value)
     * @param multiLanguageFormat   true to return all set languages for the message
     *                              in special format: <english message (if)>[{<language short>:<message>},...]
     * @param raw                   true to return the message not processed (no placeholders will be replaced)
     * @param messageType           message type to get the message for
     * @param dontAppendEncodedMsgId
     */
    getTitle( messageKey : string, language : string = this._language, placeholders : Placeholders = {}, multiLanguageFormat : boolean = false, raw : boolean = false, messageType : MessageType = 'message_catalog', dontAppendEncodedMsgId : boolean = true ) : string {
        return this.getMessage( messageKey, 'Title', language, placeholders, multiLanguageFormat, raw, messageType, dontAppendEncodedMsgId );
    }

    /**
     * Wrapper method for getContent, which returns language specific Content value of specified Message item of the
     * MetaModel collection.
     *
     * @param messageKey            CI_KEY of Message item
     * @param language              language identifier to get the message for
     */
    getMetaModelContent( messageKey : string, language : string = this._language ) : string {
        return this.getContent( messageKey, language, {}, false, false, 'metamodel' );
    }

    /**
     * Returns language specific value of specified Message item and Property type.
     *
     * @param messageKey            CI_KEY of Message item
     * @param messageContentType    CIPT_NAME of the Enum-Property to get the value from
     * @param language              language identifier to get the message for
     * @param placeholders          assoziative array with placeholders (key) and replacements (value)
     * @param multiLanguageFormat   true to return all set languages for the message
     *                              in special format: <english message (if)>[{<language short>:<message>},...]
     * @param raw                   true to return the message not processed (no placeholders will be replaced)
     * @param messageType           message type to get the message for
     * @param dontAppendEncodedMsgId
     */
    getMessage( messageKey : string, messageContentType : MessageContentType, language : string = this._language, placeholders : Placeholders = {}, multiLanguageFormat : boolean = false, raw : boolean = false, messageType : MessageType = 'message_catalog', dontAppendEncodedMsgId : boolean = false ) : string {
        let retVal = '';

        if ( this._messages[ messageType ] ) {
            const msgItem               = this._messages[ messageType ][ messageKey ],
                  languageSearchRanking = this.getLanguageSearchRanking( language );

            if ( msgItem ) {
                // get message for first matching language search rank
                if ( !multiLanguageFormat ) {
                    let messageLanguage;

                    do {
                        messageLanguage = <string> languageSearchRanking.shift();
                    }
                    while ( !msgItem[ messageContentType ][ messageLanguage ] && languageSearchRanking.length );

                    if ( !raw ) {
                        retVal = this.replacePlaceholders( msgItem[ messageContentType ][ messageLanguage ], messageLanguage, placeholders );
                    }
                    else {
                        retVal = msgItem[ messageContentType ][ messageLanguage ];
                    }

                    if ( !dontAppendEncodedMsgId && messageType !== 'format' ) {
                        retVal += this.getEncodedMessageId( messageKey, messageType, messageContentType );
                    }
                }

                // get all messages
                else {
                    for ( const messageLanguage of languageSearchRanking ) {
                        if ( msgItem[ messageContentType ][ messageLanguage ] ) {
                            if ( !retVal && !raw ) {
                                retVal = this.replacePlaceholders( msgItem[ messageContentType ][ messageLanguage ], messageLanguage, placeholders );
                            }
                            else if ( !retVal ) {
                                retVal = msgItem[ messageContentType ][ messageLanguage ];
                            }

                            else if ( !raw ) {
                                retVal += `{${messageLanguage}:${this.replacePlaceholders( msgItem[ messageContentType ][ messageLanguage ], messageLanguage, placeholders )}}`;
                            }
                            else {
                                retVal += `{${messageLanguage}:${msgItem[ messageContentType ][ messageLanguage ]}}`;
                            }
                        }
                    }
                }
            }
        }

        return retVal;
    }

    getMessageKeyForLabel( label : string ) : string {
        let retVal = label;
        const encodedId : string | undefined = label.match( /(\u200b|\u200c)+$/ )?.[ 0 ];
        if ( encodedId ) {
            const zeroWidthToBinary = ( string : string ) => (
                string.split( '' ).map( ( char : string ) : string => {
                    if ( char === '​' ) { // zero-width space
                        return '1';
                    }
                    else if ( char === '‌' ) {  // zero-width non-joiner
                        return '0';
                    }
                    return ' '; // add single space
                } ).join( '' )
            );

            const decodedId = zeroWidthToBinary( encodedId );

            const catalog : MessageType = ( { '00': 'message_catalog', '01': 'metamodel', '10': 'format', '11': 'other' }[ decodedId.slice( -2 ) ] ?? 'message_catalog' ) as MessageType;

            const msgId = Number( '0b' + decodedId.slice( 0, -4 ) );
            this._messageCatalogKeys[ catalog ] = this._messageCatalogKeys[ catalog ] ?? Object.keys( this._messages[ catalog ] ) as string[];
            retVal = this._messageCatalogKeys[ catalog ]![ msgId ];
        }
        return retVal;
    }

    private getEncodedMessageId( msgKey : string, catalog : MessageType = 'message_catalog', contentType : MessageContentType = 'Content' ) : string {
        return '';

        // eslint-disable-next-line @typescript-eslint/naming-convention
        const catalogPrefix : { [catalog in MessageType] : string } = { message_catalog: '00', metamodel: '01', format: '10', other: '11' };
        const contentTypePrefix : { [type in MessageContentType] : string } = { Content: '00', Subject: '01', Title: '10' };

        this._messageCatalogKeys[ catalog ] = this._messageCatalogKeys[ catalog ]?.length ? this._messageCatalogKeys[ catalog ] : Object.keys( this._messages[ catalog ] ) as string[];
        const msgId = this._messageCatalogKeys[ catalog ]!.indexOf( msgKey );

        const binaryToZeroWidth = ( binary : string ) : string => (
            binary.split( '' ).map( ( binaryNum ) => {
                const num = parseInt( binaryNum, 10 );
                if ( num === 1 ) {
                    return '​'; // zero-width space
                }
                else if ( num === 0 ) {
                    return '‌'; // zero-width non-joiner
                }
                return '‍'; // zero-width joiner
            } ).join( '' ) // zero-width no-break space
        );

        return msgId > 2 ? binaryToZeroWidth( msgId.toString( 2 ) + contentTypePrefix[ contentType ] + catalogPrefix[ catalog ] ) : '';
    }

    /**
     * Get the localized string for the currently set language out of a string with following format:
     * 'Default Text {DE:german text} {EN:english text} ...'
     *
     * @param text                                text in format shown above
     *
     * @return                                    localized text
     */
    private extractLocalString( text : string ) : string {
        let retVal,
            result;

        // try to get localized string with regex
        if ( /{[A-Za-z]{2,3}:/.test( text ) ) {
            const re = new RegExp( '{' + this._language + ':([\\s\\S]*?)}', 'ig' );

            // if text contains localized string (even a empty one) then return it
            if ( ( result = re.exec( text ) ) !== null ) {
                retVal = result[ 1 ];
            }

            if ( !retVal ) {
                retVal = text.substring( 0, text.indexOf( '{' ) );
            }
        }
        else {
            retVal = text;
        }

        retVal = retVal.trim();

        return retVal;
    }

    private replacePlaceholders( text : string, language : string = this._language, placeholders : {
        [ placeholder : string ] : any
    } ) : string {
        let retVal : string = text || '';

        // placeholders in retVal
        if ( retVal.match( /\$\{/ ) ) {

            // replace placeholders
            retVal = messageUtils.buildReturnFunction( { ...placeholders } )( retVal );

            // still unresolved placeholders in retVal -> replace them the old way
            if ( retVal.match( /\$\{/ ) ) {
                placeholders = {
                    ...placeholders,

                    // add placeholders for current date/time
                    currentDate: moment().format( this.getDateFormatForMomentJS( language ) ),
                    currentTime: moment().format( this.getTimeFormatForMomentJS( language ) ),
                    quarter    : moment().format( 'Q' ),
                    year       : moment().format( 'YYYY' ),
                    month      : moment().format( 'MM' ),
                    monthName  : this.getMonthName( moment().format( 'M' ), language ),
                    day        : moment().format( 'DD' ),
                    dayName    : this.getDayName( moment().format( 'E' ), language )

                    // add placeholder for environment in FRONTEND only
///////////////////////////////////////////////
                    ,
                    BASE_URL,
                    webBase: BASE_URL,
                    HOSTNAME,
                    PORT,
                    HOST,
                    ENVIRONMENT,
                    APP_TITLE,
                    APP_VERSION,
                    APP_TIME
//////////////////////////////
                };

                const placeholderKeys = Object.keys( placeholders ) as string[];

                for ( const key of placeholderKeys ) {
                    const replacement = placeholders[ key ],
                          placeholder = '${' + key + '}';

                    retVal = retVal.replaceAll( placeholder, replacement );
                }
            }
        }

        return retVal;
    }

    // language search ranking
    private getLanguageSearchRanking( language : string = this._language ) : Array<string> {
        const retVal : Array<string> = [];

        // first given one
        retVal.push( language );

        // fallback to default
        if ( retVal.indexOf( this._defaultLanguage ) === -1 ) {
            retVal.push( this._defaultLanguage );
        }

        // fallback to all supported languages
        for ( const languageId of this._supportedLanguages ) {
            if ( retVal.indexOf( languageId ) === -1 ) {
                retVal.push( languageId );
            }
        }

        return retVal;
    }
}

const messageCatalog = new MessageCatalog();

declare global {
    /* eslint-disable no-var, @typescript-eslint/naming-convention, no-underscore-dangle */
    var __ : typeof messageCatalog.translate;
    var ___ : typeof messageCatalog.getMetaModelContent;
    /* eslint-enable no-var, @typescript-eslint/naming-convention, no-underscore-dangle */
}

// eslint-disable-next-line no-underscore-dangle
global.__ = messageCatalog.translate.bind( messageCatalog );
// eslint-disable-next-line no-underscore-dangle
global.___ = messageCatalog.getMetaModelContent.bind( messageCatalog );

// eslint-disable-next-line no-underscore-dangle,@typescript-eslint/naming-convention
export const __ = messageCatalog.translate.bind( messageCatalog );

// eslint-disable-next-line no-underscore-dangle,@typescript-eslint/naming-convention
export const ___ = messageCatalog.getMetaModelContent.bind( messageCatalog );

export const getMessageKeyForLabel = messageCatalog.getMessageKeyForLabel.bind( messageCatalog );

export default messageCatalog;
